import { FunctionComponent, PropsWithChildren, useMemo } from 'react';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useTranslation } from 'react-i18next';

// Import date-fns locales
import { de } from 'date-fns/locale';
import { enUS } from 'date-fns/locale';

// Import MUI locales
import { deDE, enUS as enUSMUI } from '@mui/material/locale';
import {
    deDE as deDEDatePickers,
    enUS as enUSDatePickers,
} from '@mui/x-date-pickers/locales';

import { createStarjackTheme } from '@starjack/layout';
import { ThemeProvider } from '@mui/material/styles';

export const StarjackLocalizationProvider: FunctionComponent<
    PropsWithChildren
> = ({ children }) => {
    const { i18n } = useTranslation();

    // Get current language from i18n
    const currentLanguage = i18n.language || 'de';
    const isGerman = currentLanguage === 'de';

    // Memoize locale configurations
    const localeConfig = useMemo(() => {
        if (isGerman) {
            return {
                dateFnsLocale: de,
                muiCoreLocale: deDE,
                muiDatePickersLocale: deDEDatePickers,
            };
        }

        return {
            dateFnsLocale: enUS,
            muiCoreLocale: enUSMUI,
            muiDatePickersLocale: enUSDatePickers,
        };
    }, [isGerman]);

    const theme = useMemo(() => {
        return createStarjackTheme(localeConfig.muiCoreLocale);
    }, [localeConfig.muiCoreLocale]);

    return (
        <LocalizationProvider
            dateAdapter={AdapterDateFns}
            adapterLocale={localeConfig.dateFnsLocale}
            localeText={
                localeConfig.muiDatePickersLocale.components
                    .MuiLocalizationProvider.defaultProps.localeText
            }
        >
            <ThemeProvider theme={theme}>{children}</ThemeProvider>
        </LocalizationProvider>
    );
};
